import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import { ReactElement, memo } from 'react'
import { Edit, Delete, Image, AccessTime, Widgets } from '@mui/icons-material'
import MigrationStatusBadge from './migration-status-badge'
import { LightweightCourseV5 } from '../types'
import { useCourseDisplay } from '../hooks/use-course-display'
import { useCourseActions } from '../hooks/use-course-actions'

type CourseCardProps = {
  course: LightweightCourseV5
  categoryName: string
  onDelete: (course: LightweightCourseV5) => void
}

let CourseCard = memo(
  ({ course, categoryName, onDelete }: CourseCardProps): ReactElement => {
    let { thumbnailUrl, migrationStatus, navigationUrl } =
      useCourseDisplay(course)
    let { handleEdit, handleDelete } = useCourseActions({
      course,
      navigationUrl,
      onDelete,
    })

    return (
      <Card
        className="flex flex-col h-full transition-shadow duration-200"
        sx={{ bgcolor: 'background.paperDark' }}
      >
        <Box
          className="flex items-center justify-center h-[200px] border-b"
          sx={{
            bgcolor: 'background.paperDark',
            borderColor: 'divider',
          }}
        >
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl || undefined}
              alt={`Course thumbnail for ${course.name}`}
              className="object-cover w-full h-full"
            />
          ) : (
            <Image
              className="text-6xl"
              sx={{ color: 'text.disabled' }}
              aria-hidden="true"
            />
          )}
        </Box>
        <CardContent className="flex flex-col flex-1 p-6 pb-0">
          <Box className="flex items-start justify-between gap-2 mb-2">
            <Typography
              variant="h6"
              component="h3"
              className="line-clamp-2 font-semibold flex-1"
            >
              {course.name}
            </Typography>
            <Chip
              label={course.published === 'published' ? 'Published' : 'Draft'}
              color={course.published === 'published' ? 'success' : 'warning'}
              size="medium"
            />
          </Box>

          {categoryName && (
            <Box className="flex items-center gap-1 mb-2">
              <Widgets
                fontSize="small"
                sx={{ color: 'text.secondary' }}
                aria-hidden="true"
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {categoryName}
              </Typography>
            </Box>
          )}

          <Box className="flex flex-wrap items-center gap-4 mt-auto mb-3">
            <Box className="flex items-center">
              <AccessTime
                fontSize="small"
                className="mr-1"
                sx={{
                  color: 'text.secondary',
                }}
              />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {course.length} Hour{course.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <MigrationStatusBadge status={migrationStatus} />
          </Box>
        </CardContent>
        <Box
          className="flex items-center justify-end px-2 pt-2 pb-2 border-t"
          sx={{ borderColor: 'divider' }}
        >
          <Box className="flex gap-1">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={handleEdit}
                aria-label={`Edit ${course.name}`}
              >
                <Edit fontSize="medium" color="primary" />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={
                course.hasStudentActivity
                  ? 'Cannot delete a course with student activity'
                  : course.published === 'published'
                    ? 'Unpublish the course before deleting'
                    : 'Delete'
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={
                    course.published === 'published' ||
                    course.hasStudentActivity
                  }
                  aria-label={`Delete ${course.name}`}
                >
                  <Delete fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Card>
    )
  },
  (prevProps, nextProps) =>
    prevProps.course.id === nextProps.course.id &&
    prevProps.course.name === nextProps.course.name &&
    prevProps.course.published === nextProps.course.published &&
    prevProps.course.length === nextProps.course.length &&
    prevProps.course.thumbnail === nextProps.course.thumbnail &&
    prevProps.course.classCount === nextProps.course.classCount &&
    prevProps.course.hasStudentActivity ===
      nextProps.course.hasStudentActivity &&
    prevProps.categoryName === nextProps.categoryName,
)

export default CourseCard
